import { useCallback, useState } from 'react'
import { Grid } from 'semantic-ui-react'

import {
  Button,
  Card,
  Checkbox,
  GridRowColumn,
  Link,
  Modal,
  Text,
} from '@/componentsV1/BaseComponents'
import { getAnnualTaxFilingForYearSelector } from '../annualTaxFilings.selector'
import { useReselector } from '@/utils/sharedHooks'
import {
  needContractorFilingOption,
  updateAnnualTaxFilings,
} from '../annualTaxFilings.slice'
import { useAppDispatch } from '@/utils/typeHelpers'
import { selectCurrentAnnualTaxDetails } from '@/features/admin/annual-tax-details/annualTaxDetails.selector'
import { DATE_FORMATS_LUXON, isoToUTCDateTime } from '@/utils/dateHelpers'
import { get1099NecThresholdDisplay } from '@/features/taxes/annual-taxes/constants'

const Ten99OptOutModal = ({
  year,
  open,
  onClose,
  optIn,
}: {
  year: string
  open: boolean
  onClose: () => void
  optIn: boolean
}) => {
  const dispatch = useAppDispatch()
  const [confirmationChecked, setConfirmationChecked] = useState(false)
  const filing = useReselector(getAnnualTaxFilingForYearSelector, year)
  const annualTaxDetails = useReselector(selectCurrentAnnualTaxDetails)
  const taxDueDate = () => {
    if (optIn) {
      const dateStr = annualTaxDetails?.form_1099_nec_due_date
        ? isoToUTCDateTime(annualTaxDetails.form_1099_nec_due_date).toFormat(
            DATE_FORMATS_LUXON.DISPLAY_SHORT
          )
        : 'the deadline'
      return (
        <>
          To meet the IRS deadline, answer the questions in Heard by{' '}
          <b>{dateStr}</b>.
        </>
      )
    }
    return `Note that 1099-NEC forms are due to the IRS on ${annualTaxDetails?.form_1099_nec_irs_due_date ? isoToUTCDateTime(annualTaxDetails?.form_1099_nec_irs_due_date).toFormat(DATE_FORMATS_LUXON.DISPLAY_SHORT) : ''}. If you would like Heard to file your 1099-NECs for you, you will need to submit your information by ${annualTaxDetails?.form_1099_nec_due_date ? isoToUTCDateTime(annualTaxDetails?.form_1099_nec_due_date).toFormat(DATE_FORMATS_LUXON.DISPLAY_SHORT) : 'ASAP'}.`
  }
  const onConfirm = useCallback(async () => {
    if (!filing?.id) {
      return
    }
    const filingValue = optIn
      ? needContractorFilingOption.yes
      : needContractorFilingOption.no
    const res = await updateAnnualTaxFilings(filing?.id, {
      needContractorFiling: filingValue,
    })(dispatch)
    if (res) {
      onClose()
      location.reload()
    }
  }, [dispatch, filing?.id, optIn, onClose])
  if (!filing) {
    return null
  }

  const headerText = () => {
    if (optIn) {
      return 'Opt into 1099-NEC Filing'
    } else {
      return 'Opt out of 1099-NEC Filing'
    }
  }
  const subText = () => {
    if (optIn) {
      return "Heard partners with Taxfyle to help you file 1099-NEC forms at no cost to you. You'll answer a few questions in Heard, and a Taxfyle preparer files your forms."
    } else {
      return `To opt out of Heard preparing the ${year} Form 1099-NEC Filing for you, please check the box below.`
    }
  }
  const checkboxText = () => {
    if (optIn) {
      return 'Opt me into Form 1099-NEC tax filing through Taxfyle.'
    } else {
      return `I understand that by opting out, Heard will not file a Form 1099-NECs form on my behalf for ${year}.`
    }
  }
  return (
    <Modal open={open} onClose={onClose} closeIcon size="small">
      <Modal.Header>{headerText()}</Modal.Header>
      <Modal.Content>
        <Grid>
          <GridRowColumn>
            <Text as="bodyMd">{subText()}</Text>
            <br />
            <Text as="bodyMd">{taxDueDate()}</Text>
          </GridRowColumn>
          <GridRowColumn>
            <Card type="subsection" backgroundColor="natural">
              <Text as="h3">
                {optIn ? (
                  <>
                    You need to file Form 1099-NEC if <b>ALL</b> of the
                    following apply:
                  </>
                ) : (
                  'Who needs a 1099-NEC? (must fit all criteria below)'
                )}
              </Text>
              {optIn ? (
                <ul className="m-0 list-disc pl-5">
                  <li>
                    You paid a contractor{' '}
                    <b>at least {get1099NecThresholdDisplay(year)}</b> in {year}{' '}
                    (clinical contractors, associate therapists, billers, VAs,
                    admin support, marketing/SEO contractors).
                  </li>
                  <li>
                    You did <b>not</b> pay them through a payroll provider.
                  </li>
                  <li>
                    You paid them in cash, checks, or bank transfers (including
                    Venmo, CashApp or Zelle).
                  </li>
                </ul>
              ) : (
                <ul className="m-0 list-disc pl-5">
                  <li>
                    Their tax entity is a LLC (single-member or partnership) or
                    a sole proprietor. See box 3 on their W-9.
                  </li>
                  <li>
                    You&apos;ve paid them{' '}
                    <b>at least {get1099NecThresholdDisplay(year)}</b> in {year}
                  </li>
                  <li>
                    You&apos;ve paid them with{' '}
                    <b>cash, checks or bank transfers</b>
                  </li>
                </ul>
              )}
              {optIn && (
                <Link
                  href="https://support.joinheard.com/hc/en-us/articles/18872262968727"
                  newPage
                >
                  Learn more →
                </Link>
              )}
            </Card>
          </GridRowColumn>
          {!optIn && (
            <GridRowColumn>
              <Text>
                Still have questions? Read our{' '}
                <Link
                  href="https://support.joinheard.com/hc/en-us/articles/18872262968727"
                  newPage
                >
                  FAQs
                </Link>
              </Text>
            </GridRowColumn>
          )}

          <GridRowColumn>
            <Checkbox
              label={checkboxText()}
              checked={confirmationChecked}
              onChange={setConfirmationChecked}
            />
          </GridRowColumn>
        </Grid>
      </Modal.Content>
      <Modal.Actions>
        <Button variant="secondary" onClick={onClose} className="mr-2">
          Cancel
        </Button>
        <Button disabled={!confirmationChecked} onClick={onConfirm}>
          Confirm
        </Button>
      </Modal.Actions>
    </Modal>
  )
}

export default Ten99OptOutModal
